import { useState, useCallback } from 'react';
import { uploadCsfFile } from '../services';
import { ApiResponse } from '../types';

/**
 * Hook personalizado para gestionar la subida de archivos
 * Proporciona funciones para subir archivos junto con estados de carga y error
 */
export const useFileUpload = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  /**
   * Sube un archivo PDF de constancia fiscal
   * @param orderId - ID de la orden
   * @param file - Archivo PDF a subir
   * @param cfdiUse - Uso de CFDI
   * @param paymentMethod - Método de pago
   * @param taxRegime - Régimen fiscal
   * @returns Promesa con la respuesta de la API o undefined en caso de error
   */
  const uploadFile = useCallback(async (orderId: string, file: File, cfdiUse: string, paymentMethod: string, taxRegime: string, rfc: string): Promise<ApiResponse | undefined> => {
    if (!orderId) {
      setError('ID de orden no proporcionado');
      return undefined;
    }
    
    if (!file) {
      setError('Archivo no seleccionado');
      return undefined;
    }
    
    if (file.type !== 'application/pdf') {
      setError('Solo se permiten archivos PDF');
      return undefined;
    }

    if (!rfc) {
      setError('Debe introducir su RFC');
      return undefined;
    }
    
    if (!cfdiUse) {
      setError('Debe seleccionar el uso de CFDI');
      return undefined;
    }
    
    if (!paymentMethod) {
      setError('Debe seleccionar el método de pago');
      return undefined;
    }

    if (!taxRegime) {
      setError('Debe seleccionar el régimen fiscal');
      return undefined;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      const response = await uploadCsfFile(orderId, file, cfdiUse, paymentMethod, taxRegime, rfc);
      setSuccess(true);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Error desconocido al subir el archivo';
      setError(errorMessage);
      console.error('Error en useFileUpload:', errorMessage);
      return undefined;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    uploadFile,
    loading,
    error,
    success,
    reset: () => {
      setError(null);
      setSuccess(false);
    }
  };
}; 